import React, { useEffect, useState, useCallback } from "react";
import { AlertTriangle, X } from "lucide-react";

/**
 * Shared confirmation modal — replaces window.confirm / window.prompt / window.alert.
 *
 * Usage:
 *   const ok = await confirmDialog({ title: "Delete?", description: "...", confirmLabel: "Delete", destructive: true });
 *   const value = await promptDialog({ title: "Refund?", promptLabel: "£", confirmLabel: "Refund" });
 *   await alertDialog({ title: "Success" });
 */

const listeners = new Set();
function dispatchShow(cfg) { listeners.forEach((fn) => fn(cfg)); }

export function ConfirmProvider({ children }) {
  const [cfg, setCfg] = useState(null);

  useEffect(() => {
    listeners.add(setCfg);
    return () => listeners.delete(setCfg);
  }, []);

  return (
    <>
      {children}
      <ConfirmDialog cfg={cfg} setCfg={setCfg} />
    </>
  );
}

export function confirmDialog(cfg) {
  return new Promise((resolve) => { dispatchShow({ ...cfg, _resolve: resolve }); });
}

export function alertDialog({ title, description, confirmLabel = "OK" }) {
  return confirmDialog({ title, description, confirmLabel, hideCancel: true });
}

export function promptDialog(cfg) {
  return confirmDialog({ ...cfg, prompt: true });
}

function ConfirmDialog({ cfg, setCfg }) {
  const [val, setVal] = useState("");
  useEffect(() => { if (cfg?.prompt) setVal(cfg.defaultValue || ""); }, [cfg]);

  const close = useCallback((result) => {
    const fn = cfg?._resolve;
    if (typeof fn === "function") fn(result);
    // eslint-disable-next-line
    setCfg(null);
  }, [cfg, setCfg]);

  useEffect(() => {
    if (!cfg) return undefined;
    const handler = (e) => { if (e.key === "Escape") close(cfg.prompt ? null : false); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [cfg, close]);

  if (!cfg) return null;

  const submit = (ok) => {
    if (cfg.prompt) close(ok ? val : null);
    else close(ok);
  };

  return (
    <div
      className="fixed inset-0 z-[120] bg-black/75 backdrop-blur-sm flex items-center justify-center px-4 st-fade-up"
      onClick={() => submit(false)}
      data-testid="confirm-dialog"
    >
      <div className="st-card w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-4 mb-3">
          <div className="flex items-start gap-3">
            {cfg.destructive && (
              <div className="w-9 h-9 rounded-full bg-[#2a1212] border border-[#5b1919] flex items-center justify-center flex-shrink-0">
                <AlertTriangle size={16} className="text-[#dc2626]" />
              </div>
            )}
            <div>
              <h3 className="text-lg font-bold leading-snug">{cfg.title}</h3>
              {cfg.description && <p className="text-sm text-[#bbb] mt-1.5 leading-relaxed">{cfg.description}</p>}
            </div>
          </div>
          <button onClick={() => submit(false)} className="text-[#7a7a7a] hover:text-white p-1" data-testid="confirm-close"><X size={16}/></button>
        </div>

        {cfg.prompt && (
          <div className="mt-3">
            {cfg.promptLabel && <label className="text-xs text-[#8a8a8a]">{cfg.promptLabel}</label>}
            <input
              autoFocus type={cfg.promptType || "text"} className="st-input mt-1"
              value={val} onChange={(e) => setVal(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") submit(true); }}
              data-testid="confirm-input"
            />
          </div>
        )}

        <div className="flex justify-end gap-3 mt-6">
          {!cfg.hideCancel && (
            <button onClick={() => submit(false)} className="st-btn st-btn-ghost" data-testid="confirm-cancel">
              {cfg.cancelLabel || "Cancel"}
            </button>
          )}
          <button onClick={() => submit(true)} className="st-btn" data-testid="confirm-ok">
            {cfg.confirmLabel || "Confirm"}
          </button>
        </div>
      </div>
    </div>
  );
}
